import React, { useState, useEffect, useCallback } from 'react';
import '../../styles/ExerciseLayout.css';
import { KetMcqQuestion } from './KetReadingPart2';
import { ketNumberMap } from './ketNumbering';

const KetListenPart3 = ({ exerciseData, attemptId, exerciseId, showHint, isReviewMode, fillHintSignal }) => {
  const [answers, setAnswers] = useState({});
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    if (attemptId && exerciseId) {
      // 1. Prioritize API data in review mode
      if (isReviewMode && exerciseData.questions) {
        const migratedAnswers = {};
        exerciseData.questions.forEach(q => {
          if (q.user_answer && typeof q.user_answer.answer === 'string') {
            migratedAnswers[q.id] = q.user_answer.answer;
          }
        });
        if (Object.keys(migratedAnswers).length > 0) {
          setAnswers(migratedAnswers);
          setIsHydrated(true);
          return;
        }
      }

      // 2. Otherwise load from localStorage
      const attemptKey = `attempt_${attemptId}`;
      const savedAttempt = localStorage.getItem(attemptKey);
      if (savedAttempt) {
        try {
          const allAnswers = JSON.parse(savedAttempt);
          const savedData = allAnswers[exerciseId];
          if (savedData && typeof savedData === 'object') {
            const migratedAnswers = {};
            Object.keys(savedData).forEach(qId => {
              const value = savedData[qId];
              migratedAnswers[qId] = (typeof value === 'object' && value !== null && 'answer' in value) ? value.answer : value;
            });
            setAnswers(migratedAnswers);
          }
        } catch (e) {
          console.error("Failed to parse saved attempt from localStorage", e);
        }
      }
      setIsHydrated(true);
    }
  }, [attemptId, exerciseId, isReviewMode, exerciseData.questions]);

  const saveAnswersToLocalStorage = useCallback((currentAnswers) => {
    if (!isHydrated || !attemptId || !exerciseId || isReviewMode) return;

    const attemptKey = `attempt_${attemptId}`;
    let allAnswers = {};
    try {
      const savedAttempt = localStorage.getItem(attemptKey);
      if (savedAttempt) allAnswers = JSON.parse(savedAttempt) || {};
    } catch (e) {
      allAnswers = {};
    }

    const answersToSave = {};
    Object.keys(currentAnswers).forEach((qId) => {
      answersToSave[qId] = { answer: currentAnswers[qId] };
    });

    allAnswers[exerciseId] = answersToSave;
    localStorage.setItem(attemptKey, JSON.stringify(allAnswers));
  }, [attemptId, exerciseId, isHydrated, isReviewMode]);

  useEffect(() => {
    if (fillHintSignal > 0) {
      const newAnswers = {};
      exerciseData.questions.forEach(q => {
        if (q.accepted_answers && q.accepted_answers.length > 0) {
          newAnswers[q.id] = q.accepted_answers[0];
        }
      });
      setAnswers(newAnswers);
      saveAnswersToLocalStorage(newAnswers);
    }
  }, [fillHintSignal, exerciseData.questions, saveAnswersToLocalStorage]);

  useEffect(() => {
    if (!isHydrated) return;
    const debounceSave = setTimeout(() => {
      saveAnswersToLocalStorage(answers);
    }, 500);
    return () => {
      clearTimeout(debounceSave);
      saveAnswersToLocalStorage(answers);
    };
  }, [answers, isHydrated, saveAnswersToLocalStorage]);

  if (!exerciseData || !exerciseData.content_data || !exerciseData.questions) {
    return <div>Loading exercise data...</div>;
  }

  const sortedQuestions = [...exerciseData.questions].sort((a, b) => a.order_index - b.order_index);
  const exampleQuestion = sortedQuestions.find(q => q.points === 0);
  const mainQuestions = sortedQuestions.filter(q => q.points > 0);
  const numberMap = ketNumberMap(exerciseData);

  const handleSelect = (questionId, optionId) => {
    setAnswers(prev => ({ ...prev, [questionId]: optionId }));
  };

  return (
    <div className="w-full h-full p-1 md:px-10 overflow-y-auto custom-scrollbar">
      <div className="max-w-3xl mx-auto w-full py-4 space-y-3">
        {exampleQuestion && (
          <KetMcqQuestion
            question={exampleQuestion}
            answer={answers[exampleQuestion.id]}
            onSelect={handleSelect}
            isReviewMode={isReviewMode}
            isHydrated={isHydrated}
          />
        )}
        {mainQuestions.map((q) => (
          <KetMcqQuestion
            key={q.id}
            question={q}
            questionNumber={numberMap[q.id]}
            answer={answers[q.id]}
            onSelect={handleSelect}
            showHint={showHint}
            isReviewMode={isReviewMode}
            isHydrated={isHydrated}
          />
        ))}
      </div>
    </div>
  );
};

export default KetListenPart3;
